import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const ProblemOverviewSection = ({ data, onChange, errors }) => {
  const [charCount, setCharCount] = useState({
    title: data?.title?.length || 0,
    description: data?.description?.length || 0,
    background: data?.background?.length || 0
  });
  const [showHelperModal, setShowHelperModal] = useState(false);
  const [showCustomCategory, setShowCustomCategory] = useState(data?.category === 'other');

  const categoryOptions = [
    { value: 'transportation', label: 'Transportation & Mobility' },
    { value: 'energy', label: 'Energy & Sustainability' },
    { value: 'water', label: 'Water Management' },
    { value: 'waste', label: 'Waste Management' },
    { value: 'housing', label: 'Housing & Urban Planning' },
    { value: 'safety', label: 'Public Safety & Security' },
    { value: 'governance', label: 'Digital Governance' },
    { value: 'health', label: 'Healthcare & Wellness' },
    { value: 'education', label: 'Education & Skills' },
    { value: 'economy', label: 'Economic Development' },
    { value: 'other', label: 'Other' }
  ];

  const priorityOptions = [
    { value: 'high', label: 'High Priority' },
    { value: 'medium', label: 'Medium Priority' },
    { value: 'low', label: 'Low Priority' }
  ];

  const complexityOptions = [
    { value: 'beginner', label: 'Beginner - Basic implementation' },
    { value: 'intermediate', label: 'Intermediate - Moderate complexity' },
    { value: 'advanced', label: 'Advanced - High complexity' }
  ];

  const handleInputChange = (field, value) => {
    onChange(field, value);
    if (field === 'title' || field === 'description' || field === 'background') {
      setCharCount(prev => ({
        ...prev,
        [field]: value?.length
      }));
    }
    if (field === 'category') {
      setShowCustomCategory(value === 'other');
      if (value !== 'other') {
        onChange('customCategory', '');
      }
    }
  };

  const exampleDescriptions = [
    {
      title: 'Traffic Congestion',
      text: 'Downtown traffic congestion during weekday peak hours has increased average commute times by 25%, leading to reduced productivity, higher emissions, and increased resident complaints.'
    },
    {
      title: 'Public Service Request Backlogs',
      text: 'The city’s 311 system experiences long response times and poor issue tracking, resulting in unresolved service requests and low resident satisfaction.'
    },
    {
      title: 'Energy Inefficiency in Public Buildings',
      text: 'Municipal buildings lack real-time energy monitoring, causing excessive energy consumption and higher operating costs without clear visibility into usage patterns.'
    }
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
          <Icon name="FileText" size={16} color="white" />
        </div>
        <div>
          <h2 className="text-xl font-semibold text-foreground">Problem Overview</h2>
          <p className="text-sm text-muted-foreground">Define the core problem and context</p>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="lg:col-span-2">
          <Input
            label="Problem Title"
            type="text"
            placeholder="Enter a clear, concise problem title"
            value={data?.title || ''}
            onChange={(e) => handleInputChange('title', e?.target?.value)}
            error={errors?.title}
            required
            maxLength={100}
            description={`${charCount?.title}/100 characters`}
          />
        </div>

        <Select
          label="Problem Category"
          placeholder="Select primary category"
          options={categoryOptions}
          value={data?.category || ''}
          onChange={(value) => handleInputChange('category', value)}
          error={errors?.category}
          required
        />

        {showCustomCategory && (
          <Input
            label="Custom Category"
            type="text"
            placeholder="Enter your custom category"
            value={data?.customCategory || ''}
            onChange={(e) => handleInputChange('customCategory', e?.target?.value)}
            error={errors?.customCategory}
            required
            description="Please specify your custom category"
          />
        )}

        <Select
          label="Priority Level"
          placeholder="Select priority"
          options={priorityOptions}
          value={data?.priority || ''}
          onChange={(value) => handleInputChange('priority', value)}
          error={errors?.priority}
          required
        />

        <div className="lg:col-span-2">
          <Select
            label="Complexity Level"
            placeholder="Select complexity level"
            options={complexityOptions}
            value={data?.complexity || ''}
            onChange={(value) => handleInputChange('complexity', value)}
            error={errors?.complexity}
            required
            description="This helps match appropriate student skill levels"
          />
        </div>

        <div className="lg:col-span-2">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-foreground">
                Problem Description <span className="text-error">*</span>
              </label>
              <button
                type="button"
                onClick={() => setShowHelperModal(true)}
                className="flex items-center space-x-1 text-primary hover:text-primary/80 transition-colors"
              >
                <Icon name="HelpCircle" size={16} />
                <span className="text-xs font-medium">See Examples</span>
              </button>
            </div>
            <textarea
              className="w-full min-h-32 p-3 border border-border rounded-lg bg-input text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent resize-y"
              placeholder="Provide a detailed description of the problem, its impact on the community, and why it needs to be addressed..."
              value={data?.description || ''}
              onChange={(e) => handleInputChange('description', e?.target?.value)}
              maxLength={1000}
            />
            <div className="flex justify-between items-center">
              <span className="text-xs text-muted-foreground">
                {charCount?.description}/1000 characters
              </span>
              {errors?.description && (
                <span className="text-xs text-error">{errors?.description}</span>
              )}
            </div>
          </div>
        </div>

        <div className="lg:col-span-2">
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">
              Background & Context
            </label>
            <textarea
              className="w-full min-h-24 p-3 border border-border rounded-lg bg-input text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent resize-y"
              placeholder="Provide additional context, previous attempts, current situation, and relevant background information..."
              value={data?.background || ''}
              onChange={(e) => handleInputChange('background', e?.target?.value)}
              maxLength={500}
            />
            <div className="flex justify-between items-center">
              <span className="text-xs text-muted-foreground">
                {charCount?.background}/500 characters
              </span>
            </div>
          </div>
        </div>

        <Input
          label="Affected Population"
          type="text"
          placeholder="e.g., 50,000 residents, Downtown district"
          value={data?.affectedPopulation || ''}
          onChange={(e) => handleInputChange('affectedPopulation', e?.target?.value)}
          description="Who or what area is impacted by this problem?"
        />
      </div>

      {/* Helper Modal */}
      {showHelperModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="Lightbulb" size={20} className="text-primary" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground">Problem Description Examples</h3>
                  <p className="text-sm text-muted-foreground mt-1">Use these as inspiration for your problem statement</p>
                </div>
              </div>
              <button
                onClick={() => setShowHelperModal(false)}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {exampleDescriptions?.map((example, index) => (
                <div key={index} className="bg-muted/30 rounded-lg p-4 border border-border">
                  <div className="flex items-center space-x-2 mb-3">
                    <Icon name="FileText" size={16} className="text-primary" />
                    <h4 className="font-semibold text-foreground">{example?.title}</h4>
                  </div>
                  <p className="text-sm text-foreground leading-relaxed">{example?.text}</p>
                </div>
              ))}

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <Icon name="Info" size={20} className="text-blue-600 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-blue-900 mb-2">Tips for Writing a Good Problem Description</h4>
                    <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                      <li>Clearly state the problem and its impact</li>
                      <li>Include specific numbers and metrics when possible</li>
                      <li>Describe who is affected and how</li>
                      <li>Explain why this problem needs to be solved</li>
                      <li>Mention any current solutions and their limitations</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="sticky bottom-0 bg-card border-t border-border p-6">
              <Button
                onClick={() => setShowHelperModal(false)}
                className="w-full"
              >
                Got It
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProblemOverviewSection;